import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { useBranch } from '../../../contextApi/BranchContext';
import { usePermissions } from '../../../contextApi/PermissionContext';
import { actions } from '../../../store/store';
import useFormDraft from '../../../tools/hooks/useFormDraft';
import { DraftCloseDialog, newDraftKey, MIS_DRAFT } from '../misDraftStrip';
import PlaceSelect, { usePackingPlaces } from './placeSelect';
import { usePalletTemplates, SavePalletTemplateDialog, PalletTemplateMenu } from './palletTemplates';
import {
  EMPTY_PALLET, FIELD, Section, PalletCard, PHONE_BTN, PHONE_FORM_SX, palletCodeKey, palletForSave, palletFromRecord, palletHasContent, rowHasData,
  withKey, usePalletEditor, usePalletCodes,
} from './palletEditor';
import PalletCodeAdder from './palletCodeAdder';

const NO_SCOPE = new Set();
const NO_OPTIONS = [];
const isLocked = (p) => Boolean(p.existing && p.existing.list && p.existing.list.status === 'final');
const totalsOf = (items) => items.reduce((acc, it) => ({ pcs: acc.pcs + (Number(it.pcs) || 0), sqm: acc.sqm + (Number(it.sqm) || 0) }), { pcs: 0, sqm: 0 });

// A pallet on a final packing list: it has gone out, so it can be printed again and not changed.
function LockedPalletRow({ pallet, canRemove, onRemove, T, isXs }) {
  const { t } = useTranslation();
  const sum = totalsOf(pallet.items);
  return (
    <Box data-pallet-key={pallet._key} data-pallet-locked sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '12px', border: `1px solid ${T.BD}`, bgcolor: T.CTRL_BG }}>
      <LockOutlinedIcon sx={{ fontSize: 16, color: T.TEXT_SEC }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 700, color: T.TEXT_PRI }}>
          <span dir="ltr">{pallet.reference}</span>{pallet.palletId ? ` · ${pallet.palletId}` : ''}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: T.TEXT_SEC }}>{t('mis.lblLockedNote', { list: pallet.existing.list.docNumber })}</Typography>
      </Box>
      <Typography sx={{ fontSize: '0.72rem', color: T.TEXT_SEC, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {sum.pcs} {t('mis.plColPcs')} · {sum.sqm.toFixed(4)} {t('mis.plColSqm')}
      </Typography>
      {canRemove && (
        <Tooltip title={t('mis.plRemovePallet')}>
          <IconButton size="small" onClick={() => onRemove(pallet._key)} aria-label={t('mis.plRemovePallet')} sx={isXs ? PHONE_BTN : undefined}>
            <CloseIcon sx={{ fontSize: 16, color: T.TEXT_TER }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// The label form: everything a label shows, without a packing list. New pallets are typed here, old ones are brought in by their code, and
// the person responsible for the palleting is named once for the whole form. A label is a pallet: saving this form saves pallet records, which
// a packing list can pick later (see utils/palletRecords.js).
//   pallet (optional)  one existing pallet to change
//   draft  (optional)  an unsent form kept in this browser, to carry on with
// onClose() with nothing when it is cancelled, onClose({ saved: [{ _id, reference }], print }) after a save.
export default function PalletLabelForm({ open, onClose, pallet = null, draft = null }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const { activeBranchId } = useBranch();
  const { can } = usePermissions();

  const isEdit = Boolean(pallet);
  const branchId = isEdit ? pallet.branchId : activeBranchId;
  const canManage = can('mis:pallet:create') || can('mis:pallet:edit') || can('mis:packingList:create') || can('mis:packingList:edit');
  const canCreateVariety = can('inventory:subproduct:create');
  const { places, add: addPlace, remove: removePlace } = usePackingPlaces(branchId, open);
  const { templates, save: saveTemplate, remove: removeTemplate } = usePalletTemplates(branchId, open);

  const T = useMemo(() => ({
    BD:       isDark ? 'rgba(255,255,255,0.07)' : theme.palette.divider,
    BD2:      isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    TEXT_PRI: isDark ? 'rgba(255,255,255,0.87)' : theme.palette.text.primary,
    TEXT_SEC: isDark ? 'rgba(255,255,255,0.45)' : theme.palette.text.secondary,
    TEXT_TER: isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.3)',
    CTRL_BG:  isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    ROW_ALT:  isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    PULSE:    isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.14)',
  }), [isDark, theme.palette.divider, theme.palette.text.primary, theme.palette.text.secondary]);

  const {
    pallets, setPallets, palletsRef, specKey, setSpecKey, movedKey, announce, flip, addPallet, addFromTemplate, addExisting, onInsertKey, duplicatePallet,
    removePallet, movePalletBy, movePalletTo, setPalletField, addItem, enterRow, removeItem, setItemField, resetSqm, toggleSpec, addBuiltItem, searchForBuilder,
  } = usePalletEditor({ branchId, autoIds: false });

  const [palletizedBy, setPalletizedBy] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [people, setPeople] = useState([]);
  const [templateFor, setTemplateFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hydratedAt, setHydratedAt] = useState(0);
  const [baseline, setBaseline] = useState(null);
  const [draftKey, setDraftKey] = useState('');
  const [fromDraft, setFromDraft] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(''); setSpecKey(null); setConfirmClose(false); setFromDraft(false); setSaving(false);
    if (pallet) {
      setPallets([withKey(palletFromRecord(pallet))]);
      setPalletizedBy(pallet.palletizedBy || '');
      setDestinationAddress(pallet.destinationAddress || '');
    } else if (draft && draft.snapshot) {
      const d = draft.snapshot;
      setDraftKey(draft.key); setFromDraft(true);
      setPallets(Array.isArray(d.pallets) && d.pallets.length ? d.pallets.map(withKey) : [withKey({ ...EMPTY_PALLET })]);
      setPalletizedBy(d.palletizedBy || '');
      setDestinationAddress(d.destinationAddress || '');
    } else {
      setDraftKey(newDraftKey('label', branchId));
      setPallets([withKey({ ...EMPTY_PALLET })]);
      setPalletizedBy(''); setDestinationAddress('');
    }
    setBaseline(null);
    setHydratedAt(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pallet, draft]);

  // The names used before in this branch: the same few people do this every day.
  useEffect(() => {
    if (!open || !branchId) return undefined;
    let cancelled = false;
    authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/pallets/people`, params: { branchId } })
      .then((res) => { if (!cancelled) setPeople(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setPeople([]); });
    return () => { cancelled = true; };
  }, [open, branchId, authCtx, axiosGlobal]);

  const { codeHints, takenFor, takenLine, takenError, setTakenCodes } = usePalletCodes({ open, pallets, palletsRef, refreshOn: hydratedAt });

  // Two pallets of one form cannot share a code. There is no ID to name them by here, so a clash is shown by their place on the form.
  const clashes = useMemo(() => {
    const byKey = new Map();
    pallets.forEach((p, i) => {
      const key = palletCodeKey(p.reference);
      if (key) byKey.set(key, [...(byKey.get(key) || []), i]);
    });
    const ref = {}, values = [];
    byKey.forEach((rows) => {
      if (rows.length < 2) return;
      values.push(String(pallets[rows[0]].reference).trim());
      rows.forEach((r) => { ref[r] = rows.filter((x) => x !== r).map((x) => x + 1).join(', '); });
    });
    return { ref, values };
  }, [pallets]);

  const totals = useMemo(() => totalsOf(pallets.flatMap((p) => p.items)), [pallets]);
  const counted = pallets.filter((p) => p.palletRecordId || palletHasContent(p)).length;

  // ── the unsent draft of a NEW form ───────────────────────────────────────
  const draftable = !isEdit;
  const signature = JSON.stringify([pallets, palletizedBy, destinationAddress]);
  useEffect(() => {
    if (open && hydratedAt) setBaseline(signature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydratedAt]);
  const dirty = open && draftable && (fromDraft || (baseline !== null && signature !== baseline));
  const draftApi = useFormDraft({
    key: draftKey, kind: MIS_DRAFT.label.kind, enabled: open && draftable && Boolean(draftKey), dirty,
    snapshot: { pallets, palletizedBy, destinationAddress },
    meta: { branchId, title: [t('mis.lfLabelCount', { count: counted }), palletizedBy].filter(Boolean).join(' · ') },
  });

  const handleClose = () => {
    if (saving) return;
    if (draftable && dirty) { setConfirmClose(true); return; }
    if (draftable) draftApi.clear();
    onClose();
  };

  const keepAsTemplate = async (name) => {
    const row = palletsRef.current.find((p) => p._key === templateFor);
    if (!row) return;
    await saveTemplate({
      name, productCode: row.productCode, processingType: row.processingType,
      items: row.items.filter(rowHasData).map(({ code, sort, lengthCm, widthCm, thicknessCm, pcs, sqm }) => ({ code, sort, lengthCm, widthCm, thicknessCm, pcs, sqm })),
    });
  };
  const askTemplate = useCallback((key) => setTemplateFor(key), []);

  // New pallets take the person and the destination of the form. An old pallet keeps its own, and takes the form's only when it has none: a
  // pallet brought in to be printed must not change hands. Editing one pallet, the fields are that pallet's.
  const rowsToSend = (filled, person, place) => {
    const rows = [], sent = new Map();
    const add = (p, row) => { sent.set(p._key, rows.length); rows.push(row); };
    for (const p of filled) {
      if (isLocked(p)) continue;
      const base = palletForSave(p);
      if (!p.palletRecordId) { add(p, { ...base, palletizedBy: person, destinationAddress: place }); continue; }
      const old = p.existing;
      const changed = JSON.stringify(base) !== old.sig;
      if (isEdit) {
        if (changed || person !== old.palletizedBy || place !== old.destinationAddress) add(p, { _id: p.palletRecordId, ...base, palletizedBy: person, destinationAddress: place });
        continue;
      }
      const takesPerson = !old.palletizedBy && Boolean(person);
      const takesPlace = !old.destinationAddress && Boolean(place);
      if (changed || takesPerson || takesPlace) {
        add(p, { _id: p.palletRecordId, ...base, ...(takesPerson ? { palletizedBy: person } : {}), ...(takesPlace ? { destinationAddress: place } : {}) });
      }
    }
    return { rows, sent };
  };

  const save = async (print) => {
    const filled = pallets.filter((p) => p.palletRecordId || palletHasContent(p));
    if (!filled.length) { setError(t('mis.lblNeedsContent')); return; }
    if (clashes.values.length) { setError(clashes.values.map((code) => t('mis.lblDuplicateCode', { code })).join(' ')); return; }
    const takenHere = pallets.map((p) => takenFor(p.reference)).filter(Boolean);
    if (takenHere.length) { setError([...new Set(takenHere.map(takenError))].join(' ')); return; }

    setSaving(true); setError('');
    try {
      const { rows, sent } = rowsToSend(filled, palletizedBy.trim(), destinationAddress.trim());
      let answer = [];
      if (rows.length) {
        const res = await authCtx.jwtInst({ method: 'post', url: `${axiosGlobal.defaultTargetApi}/mis/pallets`, data: { branchId, pallets: rows } });
        answer = res.data.data || [];
      }
      const saved = filled.map((p) => (sent.has(p._key)
        ? { _id: answer[sent.get(p._key)]._id, reference: answer[sent.get(p._key)].reference }
        : { _id: p.palletRecordId, reference: p.reference }));
      if (draftable) await draftApi.clear();
      if (rows.length) dispatch(actions.setShowSnackBar({ status: true, msg: t('mis.lblSaved', { count: rows.length }), type: 'success' }));
      onClose({ saved, print });
    } catch (err) {
      const data = (err && err.response && err.response.data) || {};
      if (data.code === 'PALLET_CODE_TAKEN' && Array.isArray(data.taken)) {
        setTakenCodes((cur) => ({ ...cur, ...Object.fromEntries(data.taken.map((row) => [row.key, row])) }));
        setError(data.taken.map(takenError).join(' '));
      } else if (data.code === 'PALLET_LOCKED') {
        setError(t('mis.lblLockedError', { code: data.pallet, list: data.docNumber }));
      } else if (data.code === 'DUPLICATE_PALLET') {
        setError(t('mis.lblDuplicateCode', { code: data.value }));
      } else if (data.code === 'PALLET_NOT_FOUND') {
        setError(t('mis.lblNotFound', { codes: data.pallet }));
      } else if (data.code === 'OUT_OF_SCOPE') {
        setError(t('mis.plOutOfScopeError', { codes: (data.codes || []).join(', ') }));
      } else {
        setError(data.message || t('mis.lblSaveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const chip = { height: isXs ? 26 : 22, fontSize: isXs ? '0.74rem' : '0.65rem' };
  const editingLocked = isEdit && pallets.length === 1 && isLocked(pallets[0]);

  return (
    <>
      <Drawer anchor="right" open={open} onClose={handleClose}
        PaperProps={{ onKeyDown: (e) => onInsertKey(e, saving), 'data-testid': 'lbl-form', sx: { width: isXs ? '100vw' : 760, maxWidth: '100vw', ...(isXs ? PHONE_FORM_SX : {}) } }}>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, sm: 3 }, py: 2, borderBottom: `1px solid ${T.BD}`, flexShrink: 0 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: T.TEXT_PRI }}>{isEdit ? t('mis.lblEditTitle') : t('mis.lblNewTitle')}</Typography>
            {isEdit && <Typography dir="ltr" sx={{ fontSize: '0.7rem', color: T.TEXT_SEC, textAlign: 'start' }}>{pallet.reference}</Typography>}
          </Box>
          {draftable && draftApi.savedAt && (
            <Typography data-testid="draft-saved" sx={{ fontSize: '0.64rem', color: T.TEXT_TER, marginInlineStart: 'auto', marginInlineEnd: 1 }}>
              {t('mis.draftSaved', { time: new Date(draftApi.savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}
            </Typography>
          )}
          <IconButton onClick={handleClose} size="small" aria-label={t('common.close')} sx={isXs ? PHONE_BTN : undefined}>
            <CloseIcon sx={{ fontSize: isXs ? 22 : 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, flex: 1, overflowY: 'auto' }}>
          {!editingLocked && (
            <Section T={T} label={t('mis.lblDetailsSection')}>
              <Box sx={{ display: 'grid', gridTemplateColumns: isXs ? '1fr' : '1fr 1fr', gap: 1.25, alignItems: 'start' }}>
                {/* freeSolo: the typed text IS the stored value. Often somebody with no account in the app. */}
                <Autocomplete freeSolo options={people} inputValue={palletizedBy}
                  onInputChange={(_, v) => setPalletizedBy(v)}
                  renderInput={(params) => (
                    <TextField {...params} {...FIELD} label={t('mis.lblPalletizedBy')} helperText={t('mis.lblPalletizedByHelp')}
                      inputProps={{ ...params.inputProps, 'data-testid': 'lbl-palletized-by', maxLength: 120 }} />
                  )} />
                <PlaceSelect kind="destination" label={t('mis.lblDestinationField')} value={destinationAddress} onChange={setDestinationAddress}
                  rows={places.destination} canManage={canManage} onAdd={addPlace} onRemove={removePlace} onError={setError} touch={isXs} />
              </Box>
            </Section>
          )}

          <Section T={T} label={t('mis.lfLabelsSection')}>
            {!editingLocked && <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_SEC, mb: 1 }}>{t('mis.plCodeAutoHelp')}</Typography>}
            <Box role="status" aria-live="polite"
              sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
              {announce}
            </Box>

            <Box ref={flip.containerRef} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {pallets.map((row, pIdx) => (isLocked(row) ? (
                <LockedPalletRow key={row._key} pallet={row} canRemove={pallets.length > 1} onRemove={removePallet} T={T} isXs={isXs} />
              ) : (
                <PalletCard key={row._key || pIdx} pallet={row} index={pIdx}
                  isLast={pIdx === pallets.length - 1} canRemove={pallets.length > 1}
                  isMoved={Boolean(movedKey) && movedKey === row._key}
                  clashIdText="" clashRefText={clashes.ref[pIdx] || ''}
                  takenText={takenFor(row.reference) ? takenLine(takenFor(row.reference)) : ''}
                  codeHint={codeHints[row._key] || ''} idHint={t('mis.lblIdOptional')} canTemplate={canManage} onSaveTemplate={askTemplate}
                  isXs={isXs} T={T} type="free" scopePrefixes={NO_SCOPE} codeOptions={NO_OPTIONS}
                  specOpen={specKey === row._key} canCreateVariety={canCreateVariety} searchProducts={searchForBuilder}
                  onMoveBy={movePalletBy} onMoveTo={movePalletTo} onDuplicate={duplicatePallet} onRemove={removePallet}
                  onField={setPalletField} onAddItem={addItem} onEnterRow={enterRow} onRemoveItem={removeItem} onItemField={setItemField}
                  onResetSqm={resetSqm} onToggleSpec={toggleSpec} onUseBuilt={addBuiltItem} />
              )))}
            </Box>

            {!isEdit && (
              <>
                <Box sx={{ display: 'flex', flexDirection: isXs ? 'column' : 'row', flexWrap: 'wrap', alignItems: isXs ? 'stretch' : 'center', gap: isXs ? 1 : 0.5, mt: 1.25 }}>
                  <Button size="small" variant={isXs ? 'outlined' : 'text'} startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => addPallet()} data-testid="lbl-add-pallet"
                    sx={{ fontSize: '0.7rem', textTransform: 'none', ...(isXs ? { width: '100%', minHeight: 48, fontSize: '0.85rem', borderStyle: 'dashed' } : {}) }}>
                    {t('mis.lfAddLabel')}
                  </Button>
                  <PalletTemplateMenu templates={templates} canManage={canManage} onPick={addFromTemplate} onRemove={removeTemplate}
                    onError={setError} touch={isXs} T={T} />
                </Box>
                <PalletCodeAdder branchId={branchId} heldCodes={pallets.map((p) => p.reference)} onAdd={addExisting} touch={isXs} T={T} />
                {!isXs && <Typography sx={{ fontSize: '0.66rem', color: T.TEXT_TER, mt: 0.5 }}>{t('mis.lfKeysHint')}</Typography>}
              </>
            )}
          </Section>

          {error && <Alert severity="error" data-testid="lbl-error" sx={{ mt: 1.5, fontSize: '0.75rem', py: 0.25 }}>{error}</Alert>}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: { xs: 2, sm: 3 }, pt: 2,
          pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 2 }, borderTop: `1px solid ${T.BD}`, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip size="small" label={t('mis.lfLabelCount', { count: counted })} sx={chip} />
            <Chip size="small" label={`${totals.pcs} ${t('mis.plColPcs')}`} sx={chip} />
            <Chip size="small" label={`${totals.sqm.toFixed(4)} ${t('mis.plColSqm')}`} sx={chip} />
          </Box>
          <Box sx={{ marginInlineStart: { sm: 'auto' }, display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Button onClick={handleClose} disabled={saving} sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>{t('common.cancel')}</Button>
            {!editingLocked && (
              <Button variant="outlined" onClick={() => save(false)} disabled={saving} data-testid="lbl-save"
                sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>
                {t('common.save')}
              </Button>
            )}
            <Button variant="contained" onClick={() => save(true)} disabled={saving} data-testid="lbl-save-print"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: 'none', flex: { xs: 2, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>
              {editingLocked ? t('mis.lblPrintButton') : t('mis.lblSavePrint')}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <SavePalletTemplateDialog open={Boolean(templateFor)} pallet={templateFor ? pallets.find((p) => p._key === templateFor) : null} T={T}
        onSave={keepAsTemplate} onClose={() => setTemplateFor(null)} />

      {draftable && (
        <DraftCloseDialog open={confirmClose} onCancel={() => setConfirmClose(false)}
          onKeep={async () => { await draftApi.flush(); setConfirmClose(false); onClose(); }}
          onDiscard={async () => { await draftApi.clear(); setConfirmClose(false); onClose(); }} />
      )}
    </>
  );
}
